import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, CalendarDays, List, User, MapPin, Loader2, Share2 } from 'lucide-react';
import { fetchEventDetails } from '../api/analyticsApi';
import SignupCalendarView from './SignupCalendarView';

const toDayKey = (timestamp) => {
    if (!timestamp) return null;
    const d = new Date(timestamp);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const monthBounds = (monthDate) => {
    const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start: start.toISOString(), end: end.toISOString() };
};

const EventDetailModal = ({ isOpen, onClose, eventName, startDate, endDate, propertyId, buyerId, title, source }) => {
    // Determine if this is a signup view
    const isSignupView = eventName === 'signup_completed';
    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
    const [selectedDayKey, setSelectedDayKey] = useState(null);
    // In calendar mode, override range to the month being viewed.
    const [calendarMonth, setCalendarMonth] = useState(null);

    const inCalendarMode = isSignupView && viewMode === 'calendar' && calendarMonth;
    const effectiveStart = inCalendarMode ? monthBounds(calendarMonth).start : startDate;
    const effectiveEnd = inCalendarMode ? monthBounds(calendarMonth).end : endDate;

    useEffect(() => {
        if (isOpen) {
            setSelectedDayKey(null);
            setCalendarMonth(null);
            if (!isSignupView) setViewMode('list');
        }
    }, [isOpen, eventName, isSignupView]);

    useEffect(() => {
        if (isOpen) {
            loadEvents();
        }
    }, [isOpen, eventName, effectiveStart, effectiveEnd, propertyId, buyerId, source]);

    const filteredEvents = useMemo(() => {
        if (!selectedDayKey) return events;
        return events.filter((e) => toDayKey(e.occurred_at) === selectedDayKey);
    }, [events, selectedDayKey]);

    const loadEvents = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchEventDetails(effectiveStart, effectiveEnd, eventName, propertyId, buyerId, 100, source);
            setEvents(data);
        } catch (err) {
            console.error('Error loading events:', err);
            setError('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const isRecentSignup = (accountCreatedAt) => {
        if (!accountCreatedAt) return false;
        return Date.now() - new Date(accountCreatedAt).getTime() < 48 * 60 * 60 * 1000;
    };

    const getEventIcon = (eventName) => {
        // Return appropriate icon based on event name
        return '📊';
    };

    const getEventColor = (eventName) => {
        const colors = {
            'property_viewed': 'blue',
            'property_gallery_viewed': 'purple',
            'call_clicked': 'green',
            'email_clicked': 'orange',
            'enquiry_sent': 'red',
            'property_saved': 'pink',
            'signup_completed': 'indigo',
            'share_clicked': 'sky',
        };
        // Tailwind JIT safelist for dynamically generated badge colors:
        // bg-sky-100 text-sky-700
        return colors[eventName] || 'gray';
    };

    const formatShareMethod = (method) => {
        if (!method) return 'Unknown';
        const map = {
            native_share: 'Native Share Sheet',
            copy_link: 'Copy Link',
            copy_link_failed: 'Copy Link (failed)',
        };
        return map[method] || method.replace(/_/g, ' ');
    };

    // Human-friendly signup-source badge. Maps the raw utm_source value to a
    // readable label + emoji + colour so agents can see at a glance where a
    // buyer came from (Instagram, Facebook, Google, etc.).
    //
    // Every signup now carries a source: the portal infers one from the ad
    // click id / referrer and the backend falls back to 'direct', so the only
    // rows left without one are accounts created before that shipped, and they
    // get an explicit "Unknown" badge rather than a silently missing one.
    // Tailwind JIT safelist: bg-pink-100 text-pink-700 bg-blue-100 text-blue-700
    // bg-red-100 text-red-700 bg-emerald-100 text-emerald-700 bg-violet-100
    // text-violet-700 bg-slate-100 text-slate-700 bg-teal-100 text-teal-700
    // bg-indigo-100 text-indigo-700 bg-sky-100 text-sky-700 bg-gray-100
    // text-gray-500 bg-cyan-100 text-cyan-700 bg-amber-100 text-amber-700
    // bg-purple-100 text-purple-700 bg-orange-100 text-orange-700
    const getSourceBadge = (rawSource) => {
        const src = (rawSource || '').toLowerCase().trim();
        if (!src) {
            return {
                label: 'Unknown',
                emoji: '❓',
                className: 'bg-gray-100 text-gray-500',
                hint: 'No source recorded. This account was created before untagged signups were attributed.',
            };
        }
        const map = {
            instagram: { label: 'Instagram', emoji: '📸', className: 'bg-pink-100 text-pink-700' },
            ig: { label: 'Instagram', emoji: '📸', className: 'bg-pink-100 text-pink-700' },
            facebook: { label: 'Facebook', emoji: '👍', className: 'bg-blue-100 text-blue-700' },
            fb: { label: 'Facebook', emoji: '👍', className: 'bg-blue-100 text-blue-700' },
            // Facebook (Meta) paid ads — kept on its own 'facebook_ads' channel and a
            // distinct sky colour so paid traffic reads separately from organic
            // Facebook above. Aliases roll up the same way the backend does.
            facebook_ads: { label: 'Facebook Ads', emoji: '👍', className: 'bg-sky-100 text-sky-700' },
            fb_ads: { label: 'Facebook Ads', emoji: '👍', className: 'bg-sky-100 text-sky-700' },
            meta_ads: { label: 'Facebook Ads', emoji: '👍', className: 'bg-sky-100 text-sky-700' },
            google: { label: 'Google', emoji: '🔍', className: 'bg-red-100 text-red-700' },
            geo_seo: { label: 'Local Search (SEO)', emoji: '📍', className: 'bg-emerald-100 text-emerald-700' },
            local_seo: { label: 'Local Search (SEO)', emoji: '📍', className: 'bg-emerald-100 text-emerald-700' },
            // ChatGPT Ads (OpenAI) paid traffic — all three aliases roll up to
            // the single 'chatgpt_ads' channel on the backend (source_analytics).
            chatgpt: { label: 'ChatGPT Ads', emoji: '🤖', className: 'bg-indigo-100 text-indigo-700' },
            chatgpt_ads: { label: 'ChatGPT Ads', emoji: '🤖', className: 'bg-indigo-100 text-indigo-700' },
            openai: { label: 'ChatGPT Ads', emoji: '🤖', className: 'bg-indigo-100 text-indigo-700' },
            email: { label: 'Email', emoji: '✉️', className: 'bg-violet-100 text-violet-700' },
            sms: { label: 'SMS', emoji: '📱', className: 'bg-teal-100 text-teal-700' },
            direct: { label: 'Direct', emoji: '🔗', className: 'bg-slate-100 text-slate-700' },
            // ── Channels the portal now infers when the ad's UTM was lost ──
            // 'website' is the big one: the ad points at the Collings marketing
            // site and the hop into the portal drops the tag, so all we can
            // honestly say is that the website sent them.
            website: { label: 'Collings Website', emoji: '🏠', className: 'bg-cyan-100 text-cyan-700' },
            collings: { label: 'Collings Website', emoji: '🏠', className: 'bg-cyan-100 text-cyan-700' },
            traditional_seo: { label: 'Google (Organic)', emoji: '🔍', className: 'bg-red-100 text-red-700' },
            bing: { label: 'Bing', emoji: '🔍', className: 'bg-amber-100 text-amber-700' },
            duckduckgo: { label: 'DuckDuckGo', emoji: '🔍', className: 'bg-amber-100 text-amber-700' },
            yahoo: { label: 'Yahoo', emoji: '🔍', className: 'bg-purple-100 text-purple-700' },
            // Organic ChatGPT referral, deliberately NOT the paid 'chatgpt'
            // tag, which is the ChatGPT Ads channel.
            chatgpt_referral: { label: 'ChatGPT (Referral)', emoji: '🤖', className: 'bg-indigo-100 text-indigo-700' },
            perplexity: { label: 'Perplexity', emoji: '🤖', className: 'bg-indigo-100 text-indigo-700' },
            gemini: { label: 'Gemini', emoji: '🤖', className: 'bg-indigo-100 text-indigo-700' },
            tiktok: { label: 'TikTok', emoji: '🎵', className: 'bg-slate-100 text-slate-700' },
            linkedin: { label: 'LinkedIn', emoji: '💼', className: 'bg-blue-100 text-blue-700' },
            twitter: { label: 'X / Twitter', emoji: '🐦', className: 'bg-slate-100 text-slate-700' },
            youtube: { label: 'YouTube', emoji: '▶️', className: 'bg-red-100 text-red-700' },
            reddit: { label: 'Reddit', emoji: '👽', className: 'bg-orange-100 text-orange-700' },
            pinterest: { label: 'Pinterest', emoji: '📌', className: 'bg-red-100 text-red-700' },
            realestate_com_au: { label: 'realestate.com.au', emoji: '🏘️', className: 'bg-red-100 text-red-700' },
            domain_com_au: { label: 'Domain', emoji: '🏘️', className: 'bg-emerald-100 text-emerald-700' },
            real_estate_agent: { label: 'Agent Referral', emoji: '🧑‍💼', className: 'bg-violet-100 text-violet-700' },
        };
        return (
            map[src] || {
                label: rawSource.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                emoji: '🌐',
                className: 'bg-slate-100 text-slate-700',
            }
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 gap-4">
                    <div className="min-w-0">
                        <h2 className="text-2xl font-bold text-gray-900">{title || 'Event Details'}</h2>
                        {isSignupView && (
                            <p className="text-sm text-gray-600 mt-1">
                                📋 Showing unique buyer accounts only • Sorted by newest first •{' '}
                                <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse inline-block" />
                                    Amber = signed up within last 2 days
                                </span>
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isSignupView && (
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${
                                        viewMode === 'list'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                    title="List view"
                                >
                                    <List className="w-4 h-4" />
                                    List
                                </button>
                                <button
                                    onClick={() => setViewMode('calendar')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${
                                        viewMode === 'calendar'
                                            ? 'bg-white text-indigo-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                    title="Calendar view"
                                >
                                    <CalendarDays className="w-4 h-4" />
                                    Calendar
                                </button>
                            </div>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {isSignupView && viewMode === 'calendar' && (
                        <div className="mb-6">
                            <SignupCalendarView
                                events={events}
                                selectedDayKey={selectedDayKey}
                                onDayClick={setSelectedDayKey}
                                onMonthChange={setCalendarMonth}
                                initialMonth={calendarMonth || endDate || new Date()}
                                loading={loading}
                            />
                        </div>
                    )}
                    {loading && !(isSignupView && viewMode === 'calendar') ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : events.length === 0 && !(isSignupView && viewMode === 'calendar') ? (
                        <div className="text-center text-gray-500 py-8">No events found</div>
                    ) : (
                        <>
                            {(viewMode === 'list' || selectedDayKey) && (
                                filteredEvents.length === 0 ? (
                                    <div className="text-center text-gray-500 py-8">
                                        No signups on the selected day.
                                    </div>
                                ) : (
                        <div className="space-y-4">
                            {filteredEvents.map((event, index) => {
                                const color = getEventColor(event.event_name);
                                return (
                                    <div
                                        key={event.event_id}
                                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                {/* Event Name */}
                                                <div className="flex items-center gap-3 mb-2">
                                                    <span className={`px-3 py-1 bg-${color}-100 text-${color}-700 rounded-full text-sm font-semibold`}>
                                                        {event.event_name.replace(/_/g, ' ').toUpperCase()}
                                                    </span>
                                                    <span className="text-sm text-gray-500">
                                                        <Calendar className="w-4 h-4 inline mr-1" />
                                                        {formatTimestamp(event.occurred_at)}
                                                    </span>
                                                </div>

                                                {/* Buyer Info - Always show buyer if available */}
                                                {event.buyer ? (
                                                    <div className={`flex items-start gap-3 mb-2 p-2 rounded-lg ${
                                                        event.event_name === 'signup_completed' && isRecentSignup(event.buyer.account_created_at)
                                                            ? 'bg-amber-50 border border-amber-200'
                                                            : 'bg-blue-50'
                                                    }`}>
                                                        <User className={`w-5 h-5 mt-0.5 ${
                                                            event.event_name === 'signup_completed' && isRecentSignup(event.buyer.account_created_at)
                                                                ? 'text-amber-600'
                                                                : 'text-blue-600'
                                                        }`} />
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="text-sm font-bold text-gray-900">
                                                                    {event.buyer.name}
                                                                </span>
                                                                {event.event_name === 'signup_completed' && (
                                                                    isRecentSignup(event.buyer.account_created_at) ? (
                                                                        <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                                                                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse inline-block" />
                                                                            NEW
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                                                            Signup
                                                                        </span>
                                                                    )
                                                                )}
                                                                {/* Where the signup came from — shown right beside the NEW tag */}
                                                                {event.event_name === 'signup_completed' && (() => {
                                                                    const badge = getSourceBadge(event.utm_source);
                                                                    return badge ? (
                                                                        <span
                                                                            className={`flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${badge.className}`}
                                                                            title={
                                                                                badge.hint
                                                                                    ? badge.hint
                                                                                    : event.utm_medium
                                                                                        ? `Source: ${badge.label} · ${event.utm_medium}`
                                                                                        : `Source: ${badge.label}`
                                                                            }
                                                                        >
                                                                            <span>{badge.emoji}</span>
                                                                            {badge.label}
                                                                        </span>
                                                                    ) : null;
                                                                })()}
                                                            </div>
                                                            <div className="text-xs text-gray-600 space-y-0.5">
                                                                <div>📧 {event.buyer.email}</div>
                                                                {event.buyer.phone && (
                                                                    <div>📞 {event.buyer.phone}</div>
                                                                )}
                                                                {event.buyer.account_created_at && (
                                                                    <div className="text-gray-500 mt-1">
                                                                        Account created: {formatTimestamp(event.buyer.account_created_at)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : event.anonymous_id ? (
                                                    // Only show anonymous if no buyer AND not a signup event
                                                    event.event_name !== 'signup_completed' && (
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <User className="w-4 h-4 text-gray-400" />
                                                            <span className="text-sm text-gray-500">
                                                                Anonymous User ({event.anonymous_id.substring(0, 8)}...)
                                                            </span>
                                                        </div>
                                                    )
                                                ) : null}

                                                {/* Property Info */}
                                                {event.property && (
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <MapPin className="w-4 h-4 text-gray-400" />
                                                        <span className="text-sm text-gray-700">
                                                            {event.property.address || event.property.title}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Share Method (only for share_clicked events) */}
                                                {event.event_name === 'share_clicked' && (
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Share2 className="w-4 h-4 text-sky-500" />
                                                        <span className="text-sm text-gray-700">
                                                            Shared via{' '}
                                                            <span className="font-semibold text-sky-700">
                                                                {formatShareMethod(event.properties_json?.share_method)}
                                                            </span>
                                                        </span>
                                                    </div>
                                                )}

                                                {/* UTM Info — for signup cards this is shown as a badge
                                                    beside the name instead, so skip the raw line here. */}
                                                {event.utm_source && event.event_name !== 'signup_completed' && (
                                                    <div className="text-xs text-gray-500 mt-2">
                                                        Source: {event.utm_source}
                                                        {event.utm_medium && ` / ${event.utm_medium}`}
                                                        {event.utm_campaign && ` / ${event.utm_campaign}`}
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                                )
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                        {isSignupView ? (
                            <>
                                Showing {filteredEvents.length} unique buyer account{filteredEvents.length !== 1 ? 's' : ''}
                                {selectedDayKey ? ' on selected day' : ' (no duplicates)'}
                            </>
                        ) : (
                            <>Showing {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}</>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EventDetailModal;
